import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const UserPicker = ({ users, value, onChange, tid }) => {
  const [q, setQ] = useState("");
  const list = users.filter((u) => `${u.full_name} ${u.username} ${u.role}`.toLowerCase().includes(q.toLowerCase()));
  const allOn = list.length > 0 && list.every((u) => value.includes(u.id));
  return (
    <div className="space-y-2" data-testid={tid}>
      <div className="flex items-center gap-2">
        <Input placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid={`${tid}-search`} />
        <Button type="button" size="sm" variant="outline" onClick={() => onChange(allOn ? value.filter((id) => !list.some((u) => u.id === id)) : [...new Set([...value, ...list.map((u) => u.id)])])}>{allOn ? "Unselect all" : "Select all"}</Button>
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
        {list.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={value.includes(u.id)} onChange={(e) => onChange(e.target.checked ? [...value, u.id] : value.filter((x) => x !== u.id))} data-testid={`${tid}-user-${u.username}`} />
            <span className="text-slate-800">{u.full_name}</span><span className="text-[11px] text-slate-400 font-mono">{u.username}</span>
            <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{u.role}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const ModuleRow = ({ m, users, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [restricted, setRestricted] = useState(!!m.restricted);
  const [ids, setIds] = useState(m.allowed_user_ids || []);
  const I = Icons[m.icon] || Icons.Box;
  const dirty = restricted !== !!m.restricted || JSON.stringify([...ids].sort()) !== JSON.stringify([...(m.allowed_user_ids || [])].sort());
  const save = async () => {
    try { await api.put(`/modules/${m.module_id}/access`, { restricted, allowed_user_ids: ids }); toast.success(`${m.name} access saved`); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`module-access-${m.module_id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><I className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1"><div className="font-medium text-slate-900">{m.name}{m.coming_soon && <span className="ml-2 text-[10px] text-slate-400">Coming soon</span>}</div><div className="text-xs text-slate-500 truncate">{restricted ? `Hidden from everyone except Super Admin and ${ids.length} selected user(s)` : "Visible to users with the module permission"}</div></div>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={restricted} onCheckedChange={(v) => { setRestricted(v); if (v) setOpen(true); }} data-testid={`module-restrict-${m.module_id}`} />Restricted</label>
        {restricted && <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`module-users-${m.module_id}`}><Icons.Users className="h-3.5 w-3.5 mr-1.5" />{ids.length} user(s)<Icons.ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${open ? "rotate-180" : ""}`} /></Button>}
        <Button size="sm" disabled={!dirty} onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid={`module-save-${m.module_id}`}>Save</Button>
      </div>
      {restricted && open && <div className="border-t border-slate-100 p-4"><UserPicker users={users} value={ids} onChange={setIds} tid={`module-picker-${m.module_id}`} /></div>}
    </div>
  );
};

export function ModuleAccessTab() {
  const [mods, setMods] = useState([]);
  const [users, setUsers] = useState([]);
  const load = () => api.get("/modules/access").then(({ data }) => setMods(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/users?q=").then(({ data }) => setUsers(data.filter((u) => (u.role || "").toUpperCase() !== "SUPER ADMIN"))); }, []);
  return (
    <div className="space-y-3" data-testid="module-access">
      <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-xs text-slate-600 flex items-start gap-2"><Icons.ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5" />Turn on <b className="mx-1">Restricted</b> to hide a module from the Tenants Application Directory, sidebar and its pages/data for everyone except Super Admin and the users you select.</div>
      {mods.map((m) => <ModuleRow key={`${m.module_id}-${m.restricted}-${(m.allowed_user_ids || []).length}`} m={m} users={users} onSaved={load} />)}
    </div>
  );
}
